// The Studio backend over the HTTP API (src/core/protocol.ts).

import { PaymentRequired, Refused, SignInRequired, type PackageDownload, type StudioBackend } from "../core/studio.ts";
import type { AppInfo, DeviceId, NativeTarget, StudioSnapshot, UserInfo } from "../core/types.ts";
import type { AuthProvidersResponse, MeResponse, NativeDownloadInfo, PaymentRequiredResponse } from "../core/protocol.ts";
import { lang } from "../ui/lang.ts";

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    // The language the room shows, so a refusal comes back in it.
    headers: body === undefined ? { "x-pocket-lang": lang() } : { "content-type": "application/json", "x-pocket-lang": lang() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 402) {
    const due = (await response.json()) as PaymentRequiredResponse;
    throw new PaymentRequired(due.kind, due.id, due.price);
  }
  // A package and a game's source ask for an account: a guest's room answers with its sign-in card.
  if (response.status === 401 && ((await response.clone().json().catch(() => null)) as { error?: string } | null)?.error === "signin_required") throw new SignInRequired();
  if (!response.ok) throw await failure(response, `${method} ${path}`);
  return response.status === 204 ? (undefined as T) : (response.json() as Promise<T>);
}

/** What a failed response means: the API's own refusal with its reason, or a fault. */
async function failure(response: Response, what: string): Promise<Error> {
  const detail = await response.json().catch(() => null) as { error?: string } | null;
  if (detail?.error && response.status >= 400 && response.status < 500) return new Refused(detail.error);
  return new Error(detail?.error ?? `${what} failed with ${response.status}`);
}

/** The signed-in user, null when signed out, or "offline" when no API answers at this origin. */
export async function whoAmI(): Promise<UserInfo | null | "offline"> {
  let response: Response;
  try {
    response = await fetch("/api/me", { credentials: "same-origin" });
  } catch {
    return "offline";
  }
  if (response.status === 401) return null;
  if (!response.ok || !response.headers.get("content-type")?.includes("json")) return "offline";
  return ((await response.json()) as MeResponse).user;
}

export function providers(): Promise<AuthProvidersResponse> {
  return call("GET", "/api/auth/providers");
}

export async function signInAsGuest(): Promise<UserInfo> {
  return (await call<MeResponse>("POST", "/api/auth/guest")).user;
}

const SNAPSHOT_PARTS = ["user", "membership", "devices", "apps", "rooms"] as const;

/**
 * A server process older than this page answers with a snapshot that lacks
 * the parts added since. Fail here, by name, instead of deep in a render.
 */
function whole(snapshot: StudioSnapshot): StudioSnapshot {
  const missing = SNAPSHOT_PARTS.filter((part) => snapshot?.[part] === undefined);
  if (missing.length) throw new Error(`the Studio server is older than this page: its snapshot has no ${missing.join(", ")}. Restart the server.`);
  return snapshot;
}

export const httpBackend: StudioBackend = {
  async snapshot() {
    const snapshot = whole(await call<StudioSnapshot>("GET", "/api/studio"));
    // A share link names one project (`?app=<id>`). When it is not on this
    // visitor's shelf, it is fetched by id and joins the games the room can
    // open off the shelf, where its card offers to put it on the shelf.
    const shared = new URLSearchParams(location.search).get("app");
    const aired = snapshot.aired ?? [];
    if (shared && !snapshot.apps.some((app) => app.id === shared) && !aired.some((app) => app.id === shared)) {
      const app = await call<AppInfo>("GET", `/api/apps/${encodeURIComponent(shared)}`).catch(() => null);
      if (app) snapshot.aired = [app, ...aired];
    }
    return snapshot;
  },
  setSkin: async (device: DeviceId, skin: string, color?: string) => whole(await call<StudioSnapshot>("PUT", "/api/studio/skin", { device, skin, color })),
  setRoom: async (room: string) => whole(await call<StudioSnapshot>("PUT", "/api/studio/room", { room })),
  setTitle: async (app: string, title: string) => whole(await call<StudioSnapshot>("PUT", `/api/apps/${encodeURIComponent(app)}`, { title })),
  setPublished: async (app: string, on: boolean) => whole(await call<StudioSnapshot>("PUT", `/api/apps/${encodeURIComponent(app)}`, { status: on ? "published" : "draft" })),
  setNativeAllowed: async (app: string, on: boolean) => whole(await call<StudioSnapshot>("PUT", `/api/apps/${encodeURIComponent(app)}`, { allowNative: on })),
  deleteApp: async (app: string) => whole(await call<StudioSnapshot>("DELETE", `/api/apps/${encodeURIComponent(app)}`)),
  setHandle: async (handle: string) => whole(await call<StudioSnapshot>("PUT", "/api/studio/handle", { handle })),
  setKept: async (app: string, on: boolean, via: "link" | "tv") =>
    whole(await call<StudioSnapshot>(on ? "PUT" : "DELETE", `/api/studio/kept/${encodeURIComponent(app)}`, on ? { via } : undefined)),
  async nativeDownload(app: string, device: NativeTarget): Promise<PackageDownload> {
    const { url, filename, size } = await call<NativeDownloadInfo>("GET", `/api/apps/${encodeURIComponent(app)}/native/${device}?info=1`);
    return { url, filename, size };
  },
  setSlug: async (app: string, slug: string) => whole(await call<StudioSnapshot>("PUT", `/api/apps/${encodeURIComponent(app)}`, { slug })),
  signOut: () => call<void>("POST", "/api/auth/signout"),
};

/** A game as the visitor may see it, or null when there is none to see or no server to ask. */
export async function fetchApp(id: string): Promise<AppInfo | null> {
  try {
    return await call<AppInfo>("GET", `/api/apps/${encodeURIComponent(id)}`);
  } catch {
    return null;
  }
}
